import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { API_URL } from '../config';
import { HeaderProvider, useHeader } from '../contexts/HeaderContext';
import {
  Search, Scissors, Hammer, Flame, Zap, Wind, Package, PackageCheck, Building2,
  Truck, Footprints, ClipboardCheck, Send, Palette, Printer,
  Wrench, ShoppingBag, Users, FolderOpen, LayoutDashboard,
  BarChart3, Clock, Newspaper, FileText, LogOut, Menu, X,
  ChevronLeft, ChevronRight, Circle,
} from 'lucide-react';
import './DashboardLayout.css';

// Единая карта иконок для пунктов меню — ключ это id страницы,
// не зависит от того, что вернёт API в поле icon (эмодзи из БД игнорируем).
const MENU_ICONS = {
  'search-cloud': Search, 'search-b24': Search,
  'cutting': Scissors, 'cuttingb24': Scissors,
  'drilling': Hammer, 'drillingb24': Hammer,
  'zakalka': Flame, 'zakalkab24': Flame, 'zakalka-trips': Truck,
  'argonb24': Zap,
  'matovka': Wind, 'matovkab24': Wind,
  'sclad': Package, 'scladb24': Package,
  'loft': Building2, 'loftb24': Building2,
  'upak': PackageCheck, 'upakb24': PackageCheck,
  'pickupb24': Footprints, 'pickup1036b24': ClipboardCheck, 'sendb24': Send,
  'visualloft': Palette, 'file-print': Printer,
  'product-variations': Wrench,
  'tovar': ShoppingBag, 'users': Users, 'media-admin': FolderOpen,
  'orders-dashboard': LayoutDashboard, 'reports': BarChart3, 'facetherminal': Clock,
  'news': Newspaper, 'order-form': FileText,
};

const MenuIcon = ({ id, className }) => {
  const Icon = MENU_ICONS[id] || Circle;
  return <Icon className={className} strokeWidth={1.75} />;
};

const DashboardLayoutInner = () => {
  const { headerExtras } = useHeader();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [menuGroups, setMenuGroups] = useState([]);
  const [pageTitles, setPageTitles] = useState({});
  const [loadingPages, setLoadingPages] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, user, permissions, isLoading } = usePermissions();

  // Отладочная информация
  console.log('DashboardLayout - Current user:', user);
  console.log('DashboardLayout - User permissions:', permissions);

  // Загрузка страниц из API при монтировании компонента
  useEffect(() => {
    const fetchPages = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Загрузка страниц из API...');
        const response = await fetch(`${API_URL}/api/pages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const pages = await response.json();
          console.log('Страницы загружены из API:', pages);
          setMenuGroups(pages);

          // Создаем мапу заголовков страниц
          const titles = {};
          pages.forEach(group => {
            group.items.forEach(page => {
              titles[page.link] = page.title || page.text;
            });
          });
          setPageTitles(titles);
        } else {
          console.warn('API не вернул страницы, используем fallback');
          loadFallbackPages();
        }
      } catch (error) {
        console.error('Ошибка загрузки страниц из API, используем fallback:', error);
        loadFallbackPages();
      } finally {
        setLoadingPages(false);
      }
    };

    const loadFallbackPages = () => {
      // Fallback данные если API недоступен
      const fallbackGroups = [
        {
          id: 'search',
          title: 'Поиск',
          displayOrder: 1,
          items: [
            { id: 'search-cloud', text: 'Поиск заказов', icon: '🔍', link: '/search', title: 'Поиск заказов', displayOrder: 1 },
            { id: 'search-b24', text: 'Поиск заказов B24', icon: '🔍', link: '/search-b24', title: 'Поиск заказов B24', displayOrder: 2 },
            { id: 'orders-dashboard', text: 'Панель заказов', icon: '📊', link: '/orders-dashboard', title: 'Панель заказов', displayOrder: 3 },
            { id: 'buhgalteriya', text: 'Бухгалтерия', icon: '○', link: '/buhgalteriya', title: 'Бухгалтерия', displayOrder: 4 },
            { id: 'file-print', text: 'Печать файлов', icon: '🖨️', link: '/file-print', title: 'Печать файлов', displayOrder: 5 },
            { id: 'visualloft', text: 'Визуализатор Лофта', icon: '🎨', link: '/visualloft', title: 'Визуализатор Лофта', displayOrder: 6 }
          ]
        },
        {
          id: 'production-cloud',
          title: 'Производство (Cloud)',
          displayOrder: 2,
          items: [
            { id: 'cutting', text: 'Резка', icon: '✂️', link: '/cutting', title: 'Резка', displayOrder: 1 },
            { id: 'drilling', text: 'Сверловка', icon: '🔨', link: '/drilling', title: 'Сверловка', displayOrder: 2 },
            { id: 'zakalka', text: 'Закалка', icon: '🔥', link: '/zakalka', title: 'Закалка', displayOrder: 3 },
            { id: 'matovka', text: 'Матовка', icon: '💨', link: '/matovka', title: 'Матовка', displayOrder: 4 },
            { id: 'sclad', text: 'Склад', icon: '📦', link: '/sclad', title: 'Склад', displayOrder: 5 },
            { id: 'loft', text: 'Лофт', icon: '🏢', link: '/loft', title: 'Лофт', displayOrder: 6 },
            { id: 'upak', text: 'Упаковка', icon: '📦', link: '/upak', title: 'Упаковка', displayOrder: 7 }
          ]
        },
        {
          id: 'production-b24',
          title: 'Производство (B24)',
          displayOrder: 3,
          items: [
            { id: 'cuttingb24', text: 'Резка B24', icon: '✂️', link: '/cuttingb24', title: 'Резка B24', displayOrder: 1 },
            { id: 'drillingb24', text: 'Сверловка B24', icon: '🔨', link: '/drillingb24', title: 'Сверловка B24', displayOrder: 2 },
            { id: 'zakalkab24', text: 'Закалка B24', icon: '🔥', link: '/zakalkab24', title: 'Закалка B24', displayOrder: 3 },
            { id: 'zakalka-trips', text: 'Рейсы Закалка', icon: '🚛', link: '/zakalka-trips', title: 'Рейсы Закалка', displayOrder: 4 },
            { id: 'matovkab24', text: 'Матовка B24', icon: '💨', link: '/matovkab24', title: 'Матовка B24', displayOrder: 5 },
            { id: 'scladb24', text: 'Склад B24', icon: '📦', link: '/scladb24', title: 'Склад B24', displayOrder: 6 },
            { id: 'argonb24', text: 'Аргонная сварка', icon: '🔥', link: '/argonb24', title: 'Аргонная сварка', displayOrder: 6.5 },
            { id: 'loftb24', text: 'Лофт B24', icon: '🏢', link: '/loftb24', title: 'Лофт B24', displayOrder: 7 },
            { id: 'upakb24', text: 'Упаковка B24', icon: '📦', link: '/upakb24', title: 'Упаковка B24', displayOrder: 8 },
            { id: 'pickupb24', text: 'Самовывоз', icon: '🚶', link: '/pickupb24', title: 'Самовывоз', displayOrder: 9 },
            { id: 'sendb24', text: 'Отправка', icon: '📤', link: '/sendb24', title: 'Отправка', displayOrder: 10 }
          ]
        },
        {
          id: 'logistics',
          title: 'Логистика',
          displayOrder: 4,
          items: [
            { id: 'pickup1036b24', text: 'Приемка РМ 1036', icon: '📋', link: '/pickup1036b24', title: 'Приемка РМ 1036', displayOrder: 2 }
          ]
        },
        {
          id: 'management',
          title: 'Управление',
          displayOrder: 6,
          items: [
            { id: 'product-variations', text: 'Вариации товаров', icon: '🔧', link: '/product-variations', title: 'Вариации товаров', displayOrder: 2 },
            { id: 'tovar', text: 'Товары', icon: '🛍️', link: '/tovar', title: 'Товары', displayOrder: 3 },
            { id: 'users', text: 'Пользователи', icon: '👥', link: '/users', title: 'Пользователи', displayOrder: 4 },
            { id: 'media-admin', text: 'Медиа файлы', icon: '📁', link: '/media-admin', title: 'Медиа файлы', displayOrder: 5 }
          ]
        },
        {
          id: 'analytics',
          title: 'Аналитика',
          displayOrder: 7,
          items: [
            { id: 'reports', text: 'Отчеты', icon: '📈', link: '/reports', title: 'Отчеты', displayOrder: 2 },
            { id: 'facetherminal', text: 'Приход/Уход', icon: '⏰', link: '/facetherminal', title: 'Приход/Уход', displayOrder: 3 }
          ]
        },
        {
          id: 'other',
          title: 'Прочее',
          displayOrder: 8,
          items: [
            { id: 'news', text: 'Новости', icon: '📰', link: '/news', title: 'Новости', displayOrder: 1 },
            { id: 'order-form', text: 'Форма заказа B2B', icon: '📝', link: '/order-form', title: 'Форма заказа B2B', displayOrder: 2 }
          ]
        }
      ];

      console.log('Используем fallback данные для страниц');
      setMenuGroups(fallbackGroups);

      // Создаем мапу заголовков страниц из fallback
      const titles = {};
      fallbackGroups.forEach(group => {
        group.items.forEach(page => {
          titles[page.link] = page.title || page.text;
        });
      });
      setPageTitles(titles);
    };

    if (!isLoading) {
      fetchPages();
    }
  }, [isLoading]);

  const handleToggleMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Получаем заголовок страницы по текущему пути из загруженных данных
  const getPageTitle = () => {
    const path = location.pathname;
    return pageTitles[path] || '';
  };

  // Фильтруем меню в зависимости от разрешений пользователя
  const getMenuItems = () => {
    console.log('getMenuItems - menuGroups:', menuGroups);
    console.log('getMenuItems - permissions:', permissions);

    // Фильтруем группы и пункты меню по разрешениям пользователя
    const filteredGroups = menuGroups.map(group => ({
      ...group,
      items: group.items.filter(item => {
        // Если у пункта меню есть permission, проверяем разрешения
        if (item.id) {
          const hasAccess = hasPermission(item.id);
          console.log(`Проверка разрешения для ${item.text} (${item.id}):`, hasAccess);
          return hasAccess;
        }
        // Если нет permission (например, для новостей), показываем всем
        return true;
      })
    })).filter(group => group.items.length > 0); // Удаляем пустые группы

    console.log('getMenuItems - filteredGroups:', filteredGroups);
    return filteredGroups;
  };

  if (isLoading || loadingPages) {
    return (
      <div className="dashboard-layout">
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Загрузка разрешений...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <div className={`sidebar ${mobileMenuOpen ? 'sidebar-mobile-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-header">
          <h2 className="company-name">Luther</h2>
          <button className="close-menu-btn" onClick={handleToggleMenu}><X size={20} strokeWidth={1.75} /></button>
          <button className="collapse-toggle" onClick={handleToggleSidebar}>
            {sidebarCollapsed ? <ChevronRight size={18} strokeWidth={1.75} /> : <ChevronLeft size={18} strokeWidth={1.75} />}
          </button>
        </div>
        <div className="sidebar-content">
          <ul className="menu-list">
            {getMenuItems().map((group, groupIndex) => (
              <React.Fragment key={`group-${groupIndex}`}>
                {groupIndex > 0 && <li className="menu-divider"></li>}
                {group.items.map((item, itemIndex) => (
                  <li
                    key={`${item.link}-${itemIndex}`}
                    className={location.pathname === item.link ? 'menu-item active' : 'menu-item'}
                  >
                    <Link to={item.link} className="menu-link" onClick={() => setMobileMenuOpen(false)}>
                      <span className="menu-icon"><MenuIcon id={item.id} className="w-[18px] h-[18px]" /></span>
                      <span className="menu-text">{item.text}</span>
                    </Link>
                  </li>
                ))}
              </React.Fragment>
            ))}
          </ul>
          <div className="sidebar-footer">
            <button className="logout-btn" onClick={handleLogout}>
              <span className="menu-icon"><LogOut className="w-[18px] h-[18px]" strokeWidth={1.75} /></span>
              <span className="logout-text">Выйти</span>
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <header className="header">
          <button className="menu-toggle" onClick={handleToggleMenu}><Menu size={22} strokeWidth={1.75} /></button>
          <div className="header-titles">
            <div className="page-title">{getPageTitle()}</div>
            <span className="user-info">
              {user.username && `${user.username} (${user.role})`}
            </span>
          </div>
          {headerExtras && (
            <div className="header-extras">
              {headerExtras}
            </div>
          )}
        </header>
        <main className="content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const DashboardLayout = () => (
  <HeaderProvider>
    <DashboardLayoutInner />
  </HeaderProvider>
);

export default DashboardLayout;
